"use client"

import type React from "react"
import { useParams } from "react-router-dom"
import { useEditConnectorWizard } from "../../../hooks/useEditConnectorWizard"
import AuthenticateStep from "./WizardSteps/AuthenticateStep"
import FieldMappingStep from "./WizardSteps/FieldMappingStep"
import SyncRulesStep from "./WizardSteps/SyncRulesStep"
import type { ConnectorData} from "../../../types/edit-connector-wizard"
import "./EditConnectorWizard.css"

const EditConnectorWizard: React.FC = () => {
  const { tanentConnectorId, tanentId } = useParams<{
    tanentConnectorId: string
    tanentId: string
  }>()

  const {
    wizardState,
    platforms,
    fieldMappings,
    syncRules,
    sourceFields,
    targetFields,
    connectorData,
    loading,
    saving,
    errorStatus,
    successStatus,
    nextStep,
    prevStep,
    connectPlatform,
    updateFieldMapping,
    removeFieldMapping,
    addFieldMapping,
    toggleSyncRule,
    toggleSection,
    saveAllChanges,
  } = useEditConnectorWizard(tanentConnectorId, tanentId)

  const handleNext = async () => {
    if (wizardState.currentStep === wizardState.totalSteps) {
      await saveAllChanges()
    } else {
      nextStep()
    }
  }

  const handleBack = () => {
    if (wizardState.currentStep === 1) {
      // Navigate back to dashboard
      window.history.back()
    } else {
      prevStep()
    }
  }

  const getNextButtonText = () => {
    if (wizardState.currentStep === wizardState.totalSteps) {
      return saving ? "Saving Changes..." : "Save Changes"
    }
    if (wizardState.currentStep === 2) {
      const mappingCount = Object.keys(fieldMappings).length
      return `Continue (${mappingCount} mappings)`
    }
    return "Next"
  }

  const isNextDisabled = () => {
    if (saving) return true

    switch (wizardState.currentStep) {
      case 1:
        return !platforms.every((p) => p.connected)
      case 2:
        return Object.keys(fieldMappings).length === 0
      case 3:
        return false
      default:
        return false
    }
  }

  if (loading) {
    return (
      <div className="wizard-loading">
        <div className="loading-spinner"></div>
        <p>Loading connector details...</p>
      </div>
    )
  }

  return (
    <div className="edit-connector-wizard">
      <div className="wizard-container">
        {errorStatus && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {errorStatus}
          </div>
        )}

        {successStatus && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            {successStatus}
          </div>
        )}

        <div className="wizard-content">
          {wizardState.currentStep === 1 && <AuthenticateStep platforms={platforms} onConnect={connectPlatform} connectorData={connectorData as ConnectorData}/>}

          {wizardState.currentStep === 2 && (
            <FieldMappingStep
              connectorData={connectorData as ConnectorData}
              fieldMappings={fieldMappings}
              sourceFields={sourceFields}
              targetFields={targetFields}
              onUpdateMapping={updateFieldMapping}
              onRemoveMapping={removeFieldMapping}
              onAddMapping={addFieldMapping}
            />
          )}

          {wizardState.currentStep === 3 && (
            <SyncRulesStep syncRules={syncRules} onToggleRule={toggleSyncRule} onToggleSection={toggleSection} connectorData={connectorData as ConnectorData}/>
          )}
        </div>

        <div className="wizard-navigation">
          <button className="back-button" onClick={handleBack}>
            Back
          </button>
          <button className="next-button" onClick={handleNext} disabled={isNextDisabled()}>
            {getNextButtonText()}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditConnectorWizard
